import { useId, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Badge, type BadgeTone, Button } from "../../components/ui";

/**
 * Una sección por tipo de aviso, con su conteo y el detalle plegable.
 *
 * Con 878 lecturas estimadas, una lista plana ahoga los 3 avisos que de
 * verdad hay que mirar. Agrupado, el operador ve primero "3 montos
 * inusuales" y "878 lecturas estimadas · ver detalle" como una sola línea.
 */
export function GrupoAvisos<T>({
  titulo,
  cantidad,
  tono,
  etiqueta,
  abiertoInicial = false,
  items,
  renderItem,
  itemKey,
  maxInicial = 10,
  descripcion,
}: {
  titulo: string;
  cantidad: number;
  tono: BadgeTone;
  /** Texto del badge (ej. "Impide avanzar", "Aviso"). */
  etiqueta: string;
  abiertoInicial?: boolean;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  itemKey: (item: T) => string | number;
  maxInicial?: number;
  /** Explicación corta bajo el título (qué significa y qué hacer). */
  descripcion?: string | undefined;
}): JSX.Element {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const [todos, setTodos] = useState(false);
  const panelId = useId();
  const visibles = todos ? items : items.slice(0, maxInicial);

  return (
    <div className="rounded-md border border-neutral-200">
      <button
        type="button"
        aria-expanded={abierto}
        aria-controls={panelId}
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {abierto ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-neutral-900">
            <span className="tabular-nums">{cantidad.toLocaleString("es-CL")}</span>
            {titulo}
            <Badge tone={tono}>{etiqueta}</Badge>
          </span>
          {descripcion && (
            <span className="mt-0.5 block text-xs text-neutral-600">{descripcion}</span>
          )}
        </span>
        <span className="shrink-0 text-xs font-medium text-brand-700">
          {abierto ? "Ocultar" : "Ver detalle"}
        </span>
      </button>
      {abierto && (
        <div id={panelId} className="border-t border-neutral-100">
          <ul className="divide-y divide-neutral-100">
            {visibles.map((item) => (
              <li key={itemKey(item)} className="px-3 py-2">
                {renderItem(item)}
              </li>
            ))}
          </ul>
          {items.length > maxInicial && (
            <div className="border-t border-neutral-100 px-3 py-2">
              <Button variant="link" size="sm" onClick={() => setTodos((v) => !v)}>
                {todos
                  ? `Mostrar sólo los primeros ${maxInicial}`
                  : `Ver los ${items.length.toLocaleString("es-CL")}`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
